import type { BlockedReason, SettingsErrorCode, SuggestedWatchlistKey } from './act'
import type { VcroGettingStartedStepKey, VcroSignalSource, VcroSignalStatus } from './know-types'
import type {
  EvidenceKind,
  FactorGroup,
  RiskFactorKey,
  RiskLevel,
  VcroAction,
  VcroActionKey,
  VcroActionRun,
  VcroHiddenReason,
  VcroNextStep,
  VcroOutcomeRow,
} from './types'

/**
 * The catalogue key of every value the vCRO API sends as a stable key
 * (messages/vcro/<locale>.json). The API returns keys, never English; each
 * record below names every value its type allows, so a new engine factor,
 * kind or payload type fails `tsc` here until it has words, and
 * lib/vcro/__tests__/vcro-catalogue.guard.spec.ts fails until those words
 * exist in English, Arabic, Hindi and French.
 */

export const LEVEL_KEY: Record<RiskLevel, string> = {
  None: 'vcro.levels.none',
  Low: 'vcro.levels.low',
  Medium: 'vcro.levels.medium',
  High: 'vcro.levels.high',
  Critical: 'vcro.levels.critical',
}

/** What one factor is, in a few words (a waterfall row, a table cell). */
export const FACTOR_KEY: Record<RiskFactorKey, string> = {
  'simulation.compromised': 'vcro.factors.simulationCompromised',
  'simulation.compromisedReported': 'vcro.factors.simulationCompromisedReported',
  'simulation.clicked': 'vcro.factors.simulationClicked',
  'simulation.clickedReported': 'vcro.factors.simulationClickedReported',
  'simulation.opened': 'vcro.factors.simulationOpened',
  'simulation.ignored': 'vcro.factors.simulationIgnored',
  'simulation.reportedLate': 'vcro.factors.simulationReportedLate',
  'simulation.reported': 'vcro.factors.simulationReported',
  'simulation.reportedFast': 'vcro.factors.simulationReportedFast',
  realReport: 'vcro.factors.realReport',
  'phishTrainer.passed': 'vcro.factors.phishTrainerPassed',
  'phishTrainer.partial': 'vcro.factors.phishTrainerPartial',
  'phishTrainer.failed': 'vcro.factors.phishTrainerFailed',
  'coaching.low': 'vcro.factors.coachingLow',
  'coaching.medium': 'vcro.factors.coachingMedium',
  'coaching.high': 'vcro.factors.coachingHigh',
  'coaching.critical': 'vcro.factors.coachingCritical',
  'training.onTime': 'vcro.factors.trainingOnTime',
  'training.late': 'vcro.factors.trainingLate',
  'training.overdue': 'vcro.factors.trainingOverdue',
  'quiz.correct': 'vcro.factors.quizCorrect',
  'quiz.incorrect': 'vcro.factors.quizIncorrect',
}

/** An evidence source by name ("Simulations"). */
export const KIND_KEY: Record<EvidenceKind, string> = {
  simulation: 'vcro.kinds.simulation',
  realReport: 'vcro.kinds.realReport',
  training: 'vcro.kinds.training',
  quiz: 'vcro.kinds.quiz',
  phishTrainer: 'vcro.kinds.phishTrainer',
  coaching: 'vcro.kinds.coaching',
}

/** Why a person is not measured: one sentence per source with no evidence. */
export const MISSING_KEY: Record<EvidenceKind, string> = {
  simulation: 'vcro.score.missing.simulation',
  realReport: 'vcro.score.missing.realReport',
  training: 'vcro.score.missing.training',
  quiz: 'vcro.score.missing.quiz',
  phishTrainer: 'vcro.score.missing.phishTrainer',
  coaching: 'vcro.score.missing.coaching',
}

export const PAYLOAD_KEY: Record<VcroOutcomeRow['payloadType'], string> = {
  Link: 'vcro.payloadTypes.link',
  Attachment: 'vcro.payloadTypes.attachment',
  'Data Entry': 'vcro.payloadTypes.dataEntry',
  'QR Code': 'vcro.payloadTypes.qrCode',
  'MFA Capture': 'vcro.payloadTypes.mfaCapture',
  Reply: 'vcro.payloadTypes.reply',
}

/**
 * What "fell for" means for each payload type, in words on the page: the
 * failure the phishing reports count for it, which the API counts
 * (VCRO_FELL_FOR_ACT in apps/api/src/modules/vcro/vcro.constants.ts).
 */
export const FELL_FOR_MEANS_KEY: Record<VcroOutcomeRow['payloadType'], string> = {
  Link: 'vcro.outcomes.fellForMeans.link',
  Attachment: 'vcro.outcomes.fellForMeans.attachment',
  'Data Entry': 'vcro.outcomes.fellForMeans.dataEntry',
  'QR Code': 'vcro.outcomes.fellForMeans.qrCode',
  'MFA Capture': 'vcro.outcomes.fellForMeans.mfaCapture',
  Reply: 'vcro.outcomes.fellForMeans.reply',
}

/** A next step: its title (takes {count}) and why it helps. */
export const NEXT_STEP_KEY: Record<VcroNextStep['key'], { title: string; why: string }> = {
  finishOverdueTraining: { title: 'vcro.nextSteps.finishOverdueTraining.title', why: 'vcro.nextSteps.finishOverdueTraining.why' },
  reportNextSimulationFast: {
    title: 'vcro.nextSteps.reportNextSimulationFast.title',
    why: 'vcro.nextSteps.reportNextSimulationFast.why',
  },
  passPhishTrainer: { title: 'vcro.nextSteps.passPhishTrainer.title', why: 'vcro.nextSteps.passPhishTrainer.why' },
  answerQuizCorrectly: { title: 'vcro.nextSteps.answerQuizCorrectly.title', why: 'vcro.nextSteps.answerQuizCorrectly.why' },
  completeCoaching: { title: 'vcro.nextSteps.completeCoaching.title', why: 'vcro.nextSteps.completeCoaching.why' },
}

export const NEXT_STEP_LINK_KEY: Record<NonNullable<VcroNextStep['link']>['kind'], string> = {
  assignTraining: 'vcro.nextSteps.links.assignTraining',
  sendReminder: 'vcro.nextSteps.links.sendReminder',
  openPhishTrainer: 'vcro.nextSteps.links.openPhishTrainer',
  openCoaching: 'vcro.nextSteps.links.openCoaching',
}

export const PART_KEY: Record<'behaviour' | 'knowledge', string> = {
  behaviour: 'vcro.parts.behaviour',
  knowledge: 'vcro.parts.knowledge',
}

/** A factor group (wave 2): a heatmap column, a scorecard's top factor group, a mover's cause. */
export const FACTOR_GROUP_KEY: Record<FactorGroup, string> = {
  simulations: 'vcro.factorGroups.simulations',
  reporting: 'vcro.factorGroups.reporting',
  realReports: 'vcro.factorGroups.realReports',
  phishTrainer: 'vcro.factorGroups.phishTrainer',
  coaching: 'vcro.factorGroups.coaching',
  training: 'vcro.factorGroups.training',
  quiz: 'vcro.factorGroups.quiz',
  attitude: 'vcro.factorGroups.attitude',
}

/** The four quadrants of knowledge against behaviour (wave 2). */
export const QUADRANT_KEY: Record<'actNow' | 'slips' | 'teach' | 'safe', string> = {
  actNow: 'vcro.quadrants.actNow',
  slips: 'vcro.quadrants.slips',
  teach: 'vcro.quadrants.teach',
  safe: 'vcro.quadrants.safe',
}

/**
 * Why a bucket shows no score (wave 2 polish): a short label for the cell,
 * the sentence behind it (a tooltip, or the row on a phone), and the CSV note.
 * Each reason has its own words: a bucket withheld only so the total cannot
 * give a smaller one away is never called "too few measured".
 */
export const HIDDEN_REASON_KEY: Record<VcroHiddenReason, { label: string; why: string; csv: string }> = {
  fewPeople: {
    label: 'vcro.hiddenReasons.fewPeople.label',
    why: 'vcro.hiddenReasons.fewPeople.why',
    csv: 'vcro.hiddenReasons.fewPeople.csv',
  },
  fewMeasured: {
    label: 'vcro.hiddenReasons.fewMeasured.label',
    why: 'vcro.hiddenReasons.fewMeasured.why',
    csv: 'vcro.hiddenReasons.fewMeasured.csv',
  },
  protectsSmallerGroup: {
    label: 'vcro.hiddenReasons.protectsSmallerGroup.label',
    why: 'vcro.hiddenReasons.protectsSmallerGroup.why',
    csv: 'vcro.hiddenReasons.protectsSmallerGroup.csv',
  },
}

// ── Wave 3: act ─────────────────────────────────────────────────────────────

/** A suggested watchlist: its name (also the name it is saved under, in the viewer's language) and who it holds. */
export const SUGGESTED_WATCHLIST_KEY: Record<SuggestedWatchlistKey, { title: string; who: string }> = {
  highCritical: { title: 'vcro.watchlists.suggestions.highCritical.title', who: 'vcro.watchlists.suggestions.highCritical.who' },
  rising: { title: 'vcro.watchlists.suggestions.rising.title', who: 'vcro.watchlists.suggestions.rising.who' },
  compromisedTwice: {
    title: 'vcro.watchlists.suggestions.compromisedTwice.title',
    who: 'vcro.watchlists.suggestions.compromisedTwice.who',
  },
  neverReports: { title: 'vcro.watchlists.suggestions.neverReports.title', who: 'vcro.watchlists.suggestions.neverReports.who' },
  trainingOverdue: {
    title: 'vcro.watchlists.suggestions.trainingOverdue.title',
    who: 'vcro.watchlists.suggestions.trainingOverdue.who',
  },
  veryAttacked: { title: 'vcro.watchlists.suggestions.veryAttacked.title', who: 'vcro.watchlists.suggestions.veryAttacked.who' },
  privilegedHigh: {
    title: 'vcro.watchlists.suggestions.privilegedHigh.title',
    who: 'vcro.watchlists.suggestions.privilegedHigh.who',
  },
}

/** A recommended action: its name, who it reaches, and what running it will do (takes {count}). */
export const ACTION_KEY: Record<VcroActionKey, { title: string; who: string; willDo: string }> = {
  remindOverdueTraining: {
    title: 'vcro.actions.kinds.remindOverdueTraining.title',
    who: 'vcro.actions.kinds.remindOverdueTraining.who',
    willDo: 'vcro.actions.kinds.remindOverdueTraining.willDo',
  },
  assignCourse: {
    title: 'vcro.actions.kinds.assignCourse.title',
    who: 'vcro.actions.kinds.assignCourse.who',
    willDo: 'vcro.actions.kinds.assignCourse.willDo',
  },
  coachCompromisedTwice: {
    title: 'vcro.actions.kinds.coachCompromisedTwice.title',
    who: 'vcro.actions.kinds.coachCompromisedTwice.who',
    willDo: 'vcro.actions.kinds.coachCompromisedTwice.willDo',
  },
  briefVeryAttacked: {
    title: 'vcro.actions.kinds.briefVeryAttacked.title',
    who: 'vcro.actions.kinds.briefVeryAttacked.who',
    willDo: 'vcro.actions.kinds.briefVeryAttacked.willDo',
  },
  retestHighCritical: {
    title: 'vcro.actions.kinds.retestHighCritical.title',
    who: 'vcro.actions.kinds.retestHighCritical.who',
    willDo: 'vcro.actions.kinds.retestHighCritical.willDo',
  },
}

/** The name a draft gets (takes {date}), for the actions that make one: an announcement, a simulation. */
export const ACTION_DRAFT_NAME_KEY: Partial<Record<VcroActionKey, string>> = {
  briefVeryAttacked: 'vcro.actions.kinds.briefVeryAttacked.draftName',
  retestHighCritical: 'vcro.actions.kinds.retestHighCritical.draftName',
}

export const WHO_CAN_RUN_KEY: Record<VcroAction['whoCanRun'], string> = {
  admins: 'vcro.actions.whoCanRun.admins',
  departmentHeads: 'vcro.actions.whoCanRun.departmentHeads',
  securityTeam: 'vcro.actions.whoCanRun.securityTeam',
}

export const BLOCKED_REASON_KEY: Record<BlockedReason, string> = {
  moduleDisabled: 'vcro.actions.blocked.moduleDisabled',
  noCourseChosen: 'vcro.actions.blocked.noCourseChosen',
  noPeople: 'vcro.actions.blocked.noPeople',
}

export const RUN_STATUS_KEY: Record<VcroActionRun['status'], string> = {
  needsApproval: 'vcro.actions.status.needsApproval',
  approved: 'vcro.actions.status.approved',
  running: 'vcro.actions.status.running',
  done: 'vcro.actions.status.done',
  refused: 'vcro.actions.status.refused',
  failed: 'vcro.actions.status.failed',
}

/** The settings form's refusals: the API's errorCodes and the form's own checks read the same words. */
export const SETTINGS_ERROR_KEY: Record<SettingsErrorCode, string> = {
  vcro_settings_target_out_of_range: 'vcro.settings.errors.targetOutOfRange',
  vcro_settings_score_rise_out_of_range: 'vcro.settings.errors.scoreRiseOutOfRange',
  vcro_settings_recipient_invalid: 'vcro.settings.errors.recipientInvalid',
  vcro_settings_recipients_too_many: 'vcro.settings.errors.recipientsTooMany',
  vcro_settings_auto_limit_out_of_range: 'vcro.settings.errors.autoLimitOutOfRange',
  vcro_settings_min_group_too_small: 'vcro.settings.errors.minGroupTooSmall',
  vcro_settings_min_group_too_large: 'vcro.settings.errors.minGroupTooLarge',
}

/**
 * Every other refusal a wave 3 write can get (W3-ACT, W3-SET), in words. An
 * errorCode with no entry reads the generic "could not be done".
 */
export const ACT_ERROR_KEY: Record<string, string> = {
  vcro_action_unknown: 'vcro.actErrors.actionUnknown',
  vcro_action_blocked: 'vcro.actErrors.actionBlocked',
  vcro_action_role_required: 'vcro.actErrors.actionRoleRequired',
  vcro_course_required: 'vcro.actErrors.courseRequired',
  vcro_course_not_found: 'vcro.actErrors.courseNotFound',
  vcro_scope_not_found: 'vcro.actErrors.scopeNotFound',
  vcro_run_not_found: 'vcro.actErrors.runNotFound',
  vcro_run_not_waiting: 'vcro.actErrors.runNotWaiting',
  vcro_cannot_approve_own_run: 'vcro.actErrors.cannotApproveOwnRun',
  vcro_approver_role_required: 'vcro.actErrors.approverRoleRequired',
  vcro_refuse_reason_required: 'vcro.actErrors.refuseReasonRequired',
  vcro_watchlist_not_found: 'vcro.actErrors.watchlistNotFound',
  vcro_watchlist_name_taken: 'vcro.actErrors.watchlistNameTaken',
  vcro_suggestion_unknown: 'vcro.actErrors.suggestionUnknown',
  vcro_suggestion_already_added: 'vcro.actErrors.suggestionAlreadyAdded',
  vcro_watchlist_rule_invalid: 'vcro.actErrors.watchlistRuleInvalid',
  vcro_settings_forbidden: 'vcro.actErrors.settingsForbidden',
  vcro_scope_paused: 'vcro.actErrors.scopePaused',
  vcro_scope_too_large: 'vcro.actErrors.scopeTooLarge',
  vcro_watchlist_in_use: 'vcro.actErrors.watchlistInUse',
  course_withdrawn: 'vcro.actErrors.courseWithdrawn',
}
export const ACT_ERROR_FALLBACK_KEY = 'vcro.actErrors.generic'

/** The words for a failed wave 3 write. */
export function actErrorKey(errorCode: string | null): string {
  return (errorCode && ACT_ERROR_KEY[errorCode]) || ACT_ERROR_FALLBACK_KEY
}

/** The payload-type key of a timeline detail value, when it is one the contract names. */
export function payloadKeyOf(value: unknown): string | null {
  return typeof value === 'string' && value in PAYLOAD_KEY ? PAYLOAD_KEY[value as VcroOutcomeRow['payloadType']] : null
}

// ── Wave 4 (W4-KNOW): signals, the model, getting started ──────────────────

/** A source of evidence by name (an evidence kind's own name where it is one), and one sentence on what it is. */
export const SIGNAL_SOURCE_KEY: Record<VcroSignalSource, { name: string; what: string }> = {
  simulations: { name: 'vcro.kinds.simulation', what: 'vcro.signals.sources.simulations.what' },
  realReports: { name: 'vcro.kinds.realReport', what: 'vcro.signals.sources.realReports.what' },
  phishTrainer: { name: 'vcro.kinds.phishTrainer', what: 'vcro.signals.sources.phishTrainer.what' },
  coaching: { name: 'vcro.kinds.coaching', what: 'vcro.signals.sources.coaching.what' },
  training: { name: 'vcro.kinds.training', what: 'vcro.signals.sources.training.what' },
  quiz: { name: 'vcro.kinds.quiz', what: 'vcro.signals.sources.quiz.what' },
  surveys: { name: 'vcro.signals.sources.surveys.name', what: 'vcro.signals.sources.surveys.what' },
  smishing: { name: 'vcro.signals.sources.smishing.name', what: 'vcro.signals.sources.smishing.what' },
  vishing: { name: 'vcro.signals.sources.vishing.name', what: 'vcro.signals.sources.vishing.what' },
  emailSecurity: { name: 'vcro.signals.sources.emailSecurity.name', what: 'vcro.signals.sources.emailSecurity.what' },
  announcements: { name: 'vcro.signals.sources.announcements.name', what: 'vcro.signals.sources.announcements.what' },
  integrations: { name: 'vcro.signals.sources.integrations.name', what: 'vcro.signals.sources.integrations.what' },
}

/** A source's status: its badge, and the sentence that says what it means here. */
export const SIGNAL_STATUS_KEY: Record<VcroSignalStatus, { label: string; why: string }> = {
  live: { label: 'vcro.signals.status.live', why: 'vcro.signals.status.liveWhy' },
  noData: { label: 'vcro.signals.status.noData', why: 'vcro.signals.status.noDataWhy' },
  notConnected: { label: 'vcro.signals.status.notConnected', why: 'vcro.signals.status.notConnectedWhy' },
  moduleOff: { label: 'vcro.signals.status.moduleOff', why: 'vcro.signals.status.moduleOffWhy' },
}

/** The part of the score a source feeds (attitude reaches groups only). */
export const SIGNAL_PART_KEY: Record<'behaviour' | 'knowledge' | 'attitude', string> = {
  behaviour: 'vcro.parts.behaviour',
  knowledge: 'vcro.parts.knowledge',
  attitude: 'vcro.model.parts.attitude',
}

/**
 * A Getting started step: its title, what it asks for, its live count with
 * something ({count}, {total}) and without (none yet), and its link.
 */
export const GETTING_STARTED_STEP_KEY: Record<
  VcroGettingStartedStepKey,
  { title: string; body: string; count: string; none: string; link: string }
> = {
  sourcesLive: {
    title: 'vcro.gettingStarted.steps.sourcesLive.title',
    body: 'vcro.gettingStarted.steps.sourcesLive.body',
    count: 'vcro.gettingStarted.steps.sourcesLive.count',
    none: 'vcro.gettingStarted.steps.sourcesLive.none',
    link: 'vcro.gettingStarted.steps.sourcesLive.link',
  },
  peopleMeasured: {
    title: 'vcro.gettingStarted.steps.peopleMeasured.title',
    body: 'vcro.gettingStarted.steps.peopleMeasured.body',
    count: 'vcro.gettingStarted.steps.peopleMeasured.count',
    none: 'vcro.gettingStarted.steps.peopleMeasured.none',
    link: 'vcro.gettingStarted.steps.peopleMeasured.link',
  },
  dimensions: {
    title: 'vcro.gettingStarted.steps.dimensions.title',
    body: 'vcro.gettingStarted.steps.dimensions.body',
    count: 'vcro.gettingStarted.steps.dimensions.count',
    none: 'vcro.gettingStarted.steps.dimensions.none',
    link: 'vcro.gettingStarted.steps.dimensions.link',
  },
  watchlist: {
    title: 'vcro.gettingStarted.steps.watchlist.title',
    body: 'vcro.gettingStarted.steps.watchlist.body',
    count: 'vcro.gettingStarted.steps.watchlist.count',
    none: 'vcro.gettingStarted.steps.watchlist.none',
    link: 'vcro.gettingStarted.steps.watchlist.link',
  },
  target: {
    title: 'vcro.gettingStarted.steps.target.title',
    body: 'vcro.gettingStarted.steps.target.body',
    count: 'vcro.gettingStarted.steps.target.count',
    none: 'vcro.gettingStarted.steps.target.none',
    link: 'vcro.gettingStarted.steps.target.link',
  },
  actionRun: {
    title: 'vcro.gettingStarted.steps.actionRun.title',
    body: 'vcro.gettingStarted.steps.actionRun.body',
    count: 'vcro.gettingStarted.steps.actionRun.count',
    none: 'vcro.gettingStarted.steps.actionRun.none',
    link: 'vcro.gettingStarted.steps.actionRun.link',
  },
  reportScheduled: {
    title: 'vcro.gettingStarted.steps.reportScheduled.title',
    body: 'vcro.gettingStarted.steps.reportScheduled.body',
    count: 'vcro.gettingStarted.steps.reportScheduled.count',
    none: 'vcro.gettingStarted.steps.reportScheduled.none',
    link: 'vcro.gettingStarted.steps.reportScheduled.link',
  },
}

/** Every catalogue key this file can hand to `t()`: the catalogue guard checks each in four languages. */
export function allLabelKeys(): string[] {
  return [
    ...Object.values(LEVEL_KEY),
    ...Object.values(FACTOR_KEY),
    ...Object.values(KIND_KEY),
    ...Object.values(MISSING_KEY),
    ...Object.values(PAYLOAD_KEY),
    ...Object.values(FELL_FOR_MEANS_KEY),
    ...Object.values(NEXT_STEP_KEY).flatMap((k) => [k.title, k.why]),
    ...Object.values(NEXT_STEP_LINK_KEY),
    ...Object.values(PART_KEY),
    ...Object.values(FACTOR_GROUP_KEY),
    ...Object.values(QUADRANT_KEY),
    ...Object.values(HIDDEN_REASON_KEY).flatMap((k) => [k.label, k.why, k.csv]),
    ...Object.values(SUGGESTED_WATCHLIST_KEY).flatMap((k) => [k.title, k.who]),
    ...Object.values(ACTION_KEY).flatMap((k) => [k.title, k.who, k.willDo]),
    ...Object.values(ACTION_DRAFT_NAME_KEY).filter((k): k is string => typeof k === 'string'),
    ...Object.values(WHO_CAN_RUN_KEY),
    ...Object.values(BLOCKED_REASON_KEY),
    ...Object.values(RUN_STATUS_KEY),
    ...Object.values(SETTINGS_ERROR_KEY),
    ...Object.values(ACT_ERROR_KEY),
    ACT_ERROR_FALLBACK_KEY,
    // wave 4 (W4-KNOW)
    ...Object.values(SIGNAL_SOURCE_KEY).flatMap((k) => [k.name, k.what]),
    ...Object.values(SIGNAL_STATUS_KEY).flatMap((k) => [k.label, k.why]),
    ...Object.values(SIGNAL_PART_KEY),
    ...Object.values(GETTING_STARTED_STEP_KEY).flatMap((k) => [k.title, k.body, k.count, k.none, k.link]),
  ]
}
